import http from '@/api/http'
import { serviceCategoriesPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface BusinessServiceCategory {
  // One row from GET `/business-service-names` (cursor-paginated list).

  id: string
  // Service category id (UUID7).

  name: string
  // Display name of the service category.

  description: string | null
  // Optional human-readable description; null when absent.
}

interface BusinessServiceCategoriesResponse extends PageMeta {
  // Paginated list payload from GET `/business-service-names`.

  items: BusinessServiceCategory[]
  // Page of service categories.
}

export interface CreateBusinessServiceCategoryPayload {
  // Request body for POST `/business-service-names`.

  name: string
  description?: string
  // Omit when blank; the API treats absence and null the same way.
}

/**
 * Creates a new service category via POST `/business-service-names`.
 */
export async function createBusinessServiceCategory(
  payload: CreateBusinessServiceCategoryPayload,
): Promise<BusinessServiceCategory> {
  const { data } = await http.post<BusinessServiceCategory>('/business-service-names', payload)
  if (import.meta.env.DEV) {
    console.log('[createBusinessServiceCategory] POST /business-service-names response', data)
  }
  return data
}

export interface UpdateBusinessServiceCategoryPayload {
  name: string
  description: string | null
}

export async function updateBusinessServiceCategory(
  id: string,
  payload: UpdateBusinessServiceCategoryPayload,
): Promise<BusinessServiceCategory> {
  const { data } = await http.patch<BusinessServiceCategory>(
    `/business-service-names/${id}`,
    payload,
  )
  if (import.meta.env.DEV)
    console.log(
      `[updateBusinessServiceCategory] PATCH /business-service-names/${id} response`,
      data,
    )
  return data
}

/**
 * Fetches a page of service categories. Same cursor/limit contract as `fetchClients`.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchBusinessServiceCategories(
  cursor?: number,
): Promise<BusinessServiceCategoriesResponse> {
  const { data } = await http.get<BusinessServiceCategoriesResponse>('/business-service-names', {
    params: cursorParams(serviceCategoriesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchBusinessServiceCategories] GET /business-service-names response', data)
  }
  return data
}

/**
 * Deletes a business service category by id.
 * @param id - Service category id to delete.
 */
export async function deleteBusinessServiceCategory(id: string): Promise<void> {
  await http.delete(`/business-service-names/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteBusinessServiceCategory] DELETE /business-service-names/${id}`)
  }
}
